import { useEffect, useState, useCallback } from "react";
import { http, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Info, Zap, Target, Award, Clock, Check, Edit3, Save, X, StickyNote } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function withinWindow(from, to) {
  if (!from || !to) return true;
  const now = nowHHMM();
  return now >= from && now <= to;
}
function fmtDateFull(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function HeadToHead({ lb }) {
  if (!lb) return null;
  const a = lb.adi.total, k = lb.kudrat.total;
  const equal = a === k;
  const total = a + k;
  let aPct;
  if (equal) aPct = 50;
  else if (total <= 0) aPct = a > k ? 100 : 0;
  else aPct = Math.max(6, Math.min(94, Math.round((a / total) * 100)));
  const leader = equal ? "TIE" : a > k ? "Adi" : "Kudrat";
  return (
    <div className="nb-card p-5 md:p-8 mb-6" data-testid="head-to-head">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] md:text-xs font-mono uppercase tracking-widest text-neutral-500">Head-to-head</div>
          <div className="font-display font-black text-2xl md:text-4xl flex items-center gap-3 flex-wrap">
            {leader === "TIE" ? "Neck & neck" : (
              <>
                {(leader === "Adi" ? lb.adi.avatar_url : lb.kudrat.avatar_url) && (
                  <img src={leader === "Adi" ? lb.adi.avatar_url : lb.kudrat.avatar_url}
                       alt={leader}
                       className="w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-ink object-cover shadow-[3px_3px_0_0_#171717]" />
                )}
                <span>{leader} is winning</span>
              </>
            )}
          </div>
        </div>
        <div className="chip bg-gold">Day {lb.challenge.day} / {lb.challenge.total_days}</div>
      </div>
      <div className="h-16 md:h-24 rounded-full border-2 border-ink flex items-stretch shadow-[0_4px_0_0_#171717] md:shadow-[0_6px_0_0_#171717]">
        <div className="flex items-center justify-start pl-1 md:pl-2 gap-2 md:gap-3 text-white font-display font-black text-lg md:text-3xl transition-all duration-500 rounded-l-full overflow-hidden"
             style={{ width: `${aPct}%`, background: "#16A34A" }}>
          {lb.adi.avatar_url && (
            <img src={lb.adi.avatar_url} alt="Adi"
                 className="w-14 h-14 md:w-20 md:h-20 rounded-full border-4 border-white object-cover shrink-0 shadow-[3px_3px_0_0_#171717]" />
          )}
          <span data-testid="adi-score" className="ml-1">{Math.round(a)}</span>
          <span className="ml-1 md:ml-2 text-[10px] md:text-sm opacity-90 hidden sm:inline">ADI</span>
        </div>
        <div className="flex items-center justify-end pr-1 md:pr-2 gap-2 md:gap-3 text-white font-display font-black text-lg md:text-3xl transition-all duration-500 rounded-r-full overflow-hidden"
             style={{ width: `${100 - aPct}%`, background: "#F59E0B" }}>
          <span className="mr-1 md:mr-2 text-[10px] md:text-sm opacity-90 hidden sm:inline">KUDRAT</span>
          <span data-testid="kudrat-score">{Math.round(k)}</span>
          {lb.kudrat.avatar_url && (
            <img src={lb.kudrat.avatar_url} alt="Kudrat"
                 className="w-14 h-14 md:w-20 md:h-20 rounded-full border-4 border-white object-cover shrink-0 shadow-[3px_3px_0_0_#171717]" />
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div>
          <div className="text-[10px] md:text-xs uppercase tracking-widest text-neutral-500 font-mono">This week</div>
          <div className="font-display font-black text-base md:text-xl">Adi <span className="text-adi">{Math.round(lb.adi.week_score)}</span></div>
        </div>
        <div className="text-right">
          <div className="text-[10px] md:text-xs uppercase tracking-widest text-neutral-500 font-mono">This week</div>
          <div className="font-display font-black text-base md:text-xl">Kudrat <span className="text-kudrat">{Math.round(lb.kudrat.week_score)}</span></div>
        </div>
      </div>
    </div>
  );
}

function TimeEditor({ habit, onSave, onCancel }) {
  const [expected, setExpected] = useState(habit.expected_time || "");
  const [from, setFrom] = useState(habit.active_from || "");
  const [to, setTo] = useState(habit.active_to || "");
  return (
    <div className="mt-3 p-3 border-2 border-dashed border-ink rounded-xl bg-neutral-50">
      <div className="grid grid-cols-3 gap-2">
        <label className="text-[10px] font-mono uppercase tracking-widest">Expected
          <input type="time" className="nb-input w-full mt-1 py-2 text-sm" value={expected} onChange={(e) => setExpected(e.target.value)} data-testid={`edit-expected-${habit.key || habit.id}`} />
        </label>
        <label className="text-[10px] font-mono uppercase tracking-widest">Active from
          <input type="time" className="nb-input w-full mt-1 py-2 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`edit-from-${habit.key || habit.id}`} />
        </label>
        <label className="text-[10px] font-mono uppercase tracking-widest">Active to
          <input type="time" className="nb-input w-full mt-1 py-2 text-sm" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`edit-to-${habit.key || habit.id}`} />
        </label>
      </div>
      <div className="flex gap-2 mt-3">
        <button className="nb-btn bg-emerald-300 px-3 py-2 text-xs flex items-center gap-1" onClick={() => onSave({ expected_time: expected || null, active_from: from || null, active_to: to || null })} data-testid={`save-window-${habit.key || habit.id}`}><Save size={12} /> Save</button>
        <button className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1" onClick={onCancel}><X size={12} /> Cancel</button>
      </div>
    </div>
  );
}

const GROUP_META = { SLF: ["Self Care", "#DBEAFE"], IRN: ["Iron", "#FEE2E2"], DPW: ["Deep Work", "#FEF3C7"], STL: ["Stillness", "#E0E7FF"] };
const GROUP_KEYS = ["SLF", "IRN", "DPW", "STL"];
const LADDER_PTS = [4, 7, 10, 12];

function groupBadge(g) {
  const [label, bg] = GROUP_META[g] || [g, "#F3F4F6"];
  return <span className="chip" style={{ background: bg }}>{g} · {label}</span>;
}

function SeasonScoreCard({ summary, user }) {
  const bg = user.color === "#F59E0B" ? "#FEF3C7" : "#DCFCE7";
  const total = Number(summary?.total || 0);
  const habits = Number(summary?.habit_points || 0);
  const bonuses = Number(summary?.auto_bonus_points || 0);
  const carry = Number(summary?.carryover_points || 0);
  return (
    <div className="nb-card p-5 sm:p-6" style={{ background: bg }} data-testid="score-card">
      <div className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Season score</div>
      <div className="font-display font-black text-4xl sm:text-5xl mt-1 leading-none">
        {Math.round(total)}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <div className="chip bg-white"><Zap size={12} /> Habits: {Math.round(habits)}</div>
        <div className="chip bg-white"><Award size={12} /> Bonuses: {Math.round(bonuses)}</div>
      </div>
      <div className="flex gap-2 mt-2 flex-wrap">
        {carry > 0 && <div className="chip bg-white text-[11px]">Carryover: +{Math.round(carry)}</div>}
        <div className="chip bg-white text-[11px]">Perfect days: {summary?.perfect_days_total || 0}</div>
      </div>
    </div>
  );
}

function WeekScoreCard({ summary }) {
  if (!summary?.weeks?.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const cur = summary.weeks.find((w) => w.start <= today && today <= w.end) || summary.weeks[0];
  return (
    <div className="nb-card p-5 sm:p-6" data-testid="week-score-card">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Week {cur.week} score</div>
        <div className="chip bg-white text-[10px]">{cur.days} days</div>
      </div>
      <div className="font-display font-black text-3xl sm:text-4xl mt-1 leading-none">
        {Math.round(cur.week_score)}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
        <div className="chip bg-emerald-50">Habits: {Math.round(cur.earned)}</div>
        <div className="chip bg-amber-50">Bonuses: +{Math.round(cur.bonus_raw)}</div>
      </div>
      <div className="text-[10px] mt-3 text-neutral-500 font-mono">{cur.start} → {cur.end}</div>
      <div className="text-[10px] mt-1 text-neutral-500">Habit completion: {Number(cur.habit_pct).toFixed(0)}%</div>
    </div>
  );
}

function WeeksStrip({ summary }) {
  if (!summary?.weeks?.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="nb-card p-4 sm:p-5" data-testid="weeks-strip">
      <div className="text-[10px] uppercase tracking-widest font-mono text-neutral-500 mb-2">Season timeline</div>
      <div className="grid grid-cols-4 gap-2">
        {summary.weeks.map((w) => {
          const active = w.start <= today && today <= w.end;
          const done = w.end < today;
          return (
            <div key={w.week} className={`border-2 border-ink rounded-xl p-2 text-center transition-colors ${active ? "bg-gold" : done ? "bg-emerald-50" : "bg-neutral-50"}`}>
              <div className="text-[9px] font-mono uppercase tracking-widest text-neutral-600">W{w.week}{w.week === 4 ? " · Finals" : ""}</div>
              <div className="font-display font-black text-lg leading-none mt-0.5">{Math.round(w.week_score)}</div>
              <div className="text-[9px] text-neutral-500 mt-1">{Math.round(w.earned)}h + {Math.round(w.bonus_raw)}b</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GroupStreaksCard({ summary }) {
  if (!summary?.weeks?.length) return null;
  // Latest completed week snapshot
  const last = [...summary.weeks].reverse().find((w) => w.complete) || summary.weeks[0];
  return (
    <div className="nb-card p-4 sm:p-5" data-testid="group-streaks">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Group streaks</div>
        <div className="chip bg-white text-[10px]">as of W{last.week}</div>
      </div>
      <ul className="space-y-1.5">
        {GROUP_KEYS.map((g) => {
          const info = last.groups?.[g];
          const held = info?.held === true;
          const broken = info?.held === false;
          const len = info?.streak_len || 0;
          const nextPts = LADDER_PTS[Math.min(len, 3)];
          const [label, bg] = GROUP_META[g] || [g, "#F3F4F6"];
          return (
            <li key={g} className="flex items-center justify-between gap-2 text-xs">
              <span className="chip shrink-0" style={{ background: bg }}>{g} · {label}</span>
              <span className={`chip ${held ? "bg-emerald-100" : broken ? "bg-rose-50" : "bg-white"}`}>
                {held ? `✓ ${len}wk · +${LADDER_PTS[Math.min(len - 1, 3)]}` : broken ? "reset" : "…"}
              </span>
              {held && len < 4 && <span className="text-[9px] text-neutral-400 shrink-0">next +{nextPts}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function HabitCard({ habit, streak, onMark, canMark, canEditContent, onUpdateWindow, onUpdateContent }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(false);
  const [desc, setDesc] = useState(habit.description || "");
  const [notes, setNotes] = useState(habit.notes || "");
  const current = habit.log?.status;
  const active = withinWindow(habit.active_from, habit.active_to);
  const isComplete = current === "full";
  const isPartial = current === "partial";
  const isPartialLow = current === "partial_low";
  const isShadow = !!habit.shadow;

  const btn = (val, label, bg) => (
    <button
      onClick={() => onMark(habit, val)}
      disabled={!canMark || (!active && !current)}
      className={`nb-btn flex-1 py-2.5 sm:py-3 text-xs sm:text-sm ${current === val ? "pressed" : ""}`}
      style={{ background: bg, color: "#171717" }}
      data-testid={`habit-${val}-${habit.key || habit.id}`}
    >
      {label}
    </button>
  );

  return (
    <>
      <motion.div layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className={`nb-card p-4 sm:p-5 relative ${isShadow ? "opacity-90" : ""}`}
        data-testid={`habit-card-${habit.key || habit.id}`}>
        <AnimatePresence>
          {isComplete && !isShadow && (
            <motion.div
              key="stamp"
              initial={{ scale: 0, rotate: -15, opacity: 0 }}
              animate={{ scale: 1, rotate: -6, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 14 }}
              className="absolute top-3 right-3 z-10">
              <div className="flex items-center gap-1 bg-emerald-500 text-white border-2 border-ink rounded-full pl-2 pr-3 py-1 shadow-[3px_3px_0_0_#171717] font-display font-black uppercase tracking-widest text-[10px] sm:text-[11px]">
                <Check size={13} strokeWidth={3.5} /> Complete
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={`transition-colors duration-200 ${isComplete ? "opacity-95" : ""}`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <button type="button" onClick={() => setOpen(true)}
                    className="flex-1 text-left group"
                    data-testid={`habit-open-${habit.key || habit.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`font-display font-bold text-lg leading-tight group-hover:underline decoration-2 underline-offset-4 ${isComplete ? "line-through decoration-emerald-500 decoration-[3px]" : ""}`}>{habit.name}</h3>
                {isShadow && <span className="chip bg-neutral-200 text-neutral-700 text-[10px]">SHADOW · streak only</span>}
                <Info size={16} className="text-neutral-500 group-hover:text-ink shrink-0" />
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {habit.group && groupBadge(habit.group)}
                {!isShadow && <span className="chip bg-white">{habit.full} pts</span>}
                {habit.partial > 0 && <span className="chip bg-yellow-100">Partial: {habit.partial}</span>}
                {habit.partial_low > 0 && <span className="chip bg-yellow-50">Partial+: {habit.partial_low}</span>}
                {streak > 0 && <span className="chip bg-orange-100"><Flame size={12} /> {streak}-day</span>}
                {habit.expected_time && <span className="chip bg-blue-50"><Clock size={12} /> {habit.expected_time}</span>}
                {(habit.active_from && habit.active_to) && (
                  <span className={`chip ${active ? "bg-emerald-50" : "bg-neutral-100"}`}>
                    {habit.active_from}–{habit.active_to}
                  </span>
                )}
                {habit.log?.marked_at && <span className="chip bg-white text-neutral-500 text-[10px]">Marked {fmtTime(habit.log.marked_at)}</span>}
              </div>
              {canEditContent && (
                <div className="mt-2 text-sm text-neutral-700 line-clamp-2" data-testid={`habit-desc-preview-${habit.key || habit.id}`}>
                  {habit.description
                    ? habit.description
                    : <span className="italic text-neutral-400">Tap to add a private description &amp; notes…</span>}
                </div>
              )}
            </button>
            {canEditContent && (
              <button onClick={() => setEditing((v) => !v)} className="text-neutral-500 hover:text-ink shrink-0 mt-1" data-testid={`habit-edit-time-${habit.key || habit.id}`}>
                <Edit3 size={14} />
              </button>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            {btn("full", isShadow ? "Log" : `Full · ${habit.full}`, isComplete ? "#10B981" : "#D1FAE5")}
            {habit.partial > 0 && btn("partial", `Partial · ${habit.partial}`, isPartial ? "#FACC15" : "#FEF3C7")}
            {habit.partial_low > 0 && btn("partial_low", `Partial+ · ${habit.partial_low}`, isPartialLow ? "#FDE68A" : "#FFFBEB")}
            {btn("zero", "Zero", current === "zero" ? "#F43F5E" : "#FECDD3")}
          </div>

          {!active && !current && (
            <div className="mt-3 text-xs font-mono uppercase tracking-widest text-neutral-500 flex items-center gap-1">
              <Clock size={12} /> Available {habit.active_from}–{habit.active_to}
            </div>
          )}

          {editing && canEditContent && (
            <TimeEditor habit={habit} onCancel={() => setEditing(false)}
              onSave={async (patch) => { await onUpdateWindow(habit, patch); setEditing(false); }} />
          )}
        </div>
      </motion.div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-2 border-ink rounded-3xl shadow-[0_12px_0_0_#171717] bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">{habit.name}</DialogTitle>
            <DialogDescription className="text-neutral-700 text-base pt-2">
              {canEditContent
                ? "Your private description and daily notes. Admin can't see this."
                : "Description and notes are private to the habit owner."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="nb-card p-4">
              <div className="text-xs uppercase tracking-widest font-mono text-neutral-500">Full</div>
              <div className="font-display font-black text-2xl">{habit.full} pts</div>
            </div>
            <div className="nb-card p-4">
              <div className="text-xs uppercase tracking-widest font-mono text-neutral-500">Partial</div>
              <div className="font-display font-black text-2xl">{habit.partial > 0 ? `${habit.partial} pts` : "—"}</div>
              {habit.partial_label && <div className="text-xs text-neutral-500 mt-1">{habit.partial_label}</div>}
            </div>
            {habit.expected_time && (
              <div className="nb-card p-4">
                <div className="text-xs uppercase tracking-widest font-mono text-neutral-500">Expected time</div>
                <div className="font-display font-black text-2xl">{habit.expected_time}</div>
              </div>
            )}
            {habit.active_from && habit.active_to && (
              <div className="nb-card p-4">
                <div className="text-xs uppercase tracking-widest font-mono text-neutral-500">Active window</div>
                <div className="font-display font-black text-2xl">{habit.active_from}–{habit.active_to}</div>
              </div>
            )}
          </div>

          <div className="mt-4 space-y-3 max-h-[50vh] overflow-y-auto">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-mono uppercase tracking-widest text-neutral-500">Description</label>
                {canEditContent && !editContent && (
                  <button onClick={() => setEditContent(true)} className="text-xs font-bold text-ink underline"
                          data-testid={`edit-content-${habit.key || habit.id}`}>Edit</button>
                )}
              </div>
              {canEditContent && editContent ? (
                <textarea className="nb-input w-full text-sm" rows="3" value={desc}
                          onChange={(e) => setDesc(e.target.value)}
                          data-testid={`desc-input-${habit.key || habit.id}`}
                          placeholder="What does this habit mean to you?" />
              ) : (
                <div className="text-sm text-neutral-700 min-h-[2rem] whitespace-pre-wrap">
                  {canEditContent ? (habit.description || <span className="italic text-neutral-400">Add a description…</span>) :
                                    <span className="italic text-neutral-400">Private to the owner.</span>}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-mono uppercase tracking-widest text-neutral-500 flex items-center gap-1">
                  <StickyNote size={12} /> Notes
                </label>
              </div>
              {canEditContent && editContent ? (
                <textarea className="nb-input w-full text-sm" rows="5" value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          data-testid={`notes-input-${habit.key || habit.id}`}
                          placeholder="Personal notes for this habit — reflections, plans, reminders." />
              ) : (
                <div className="text-sm text-neutral-700 min-h-[3rem] whitespace-pre-wrap">
                  {canEditContent ? (habit.notes || <span className="italic text-neutral-400">No notes yet.</span>) :
                                    <span className="italic text-neutral-400">Private to the owner.</span>}
                </div>
              )}
            </div>
          </div>

          {canEditContent && editContent && (
            <div className="flex gap-2 mt-3">
              <button className="nb-btn bg-emerald-300 px-3 py-2 text-xs flex items-center gap-1"
                      onClick={async () => { await onUpdateContent(habit, { description: desc, notes }); setEditContent(false); }}
                      data-testid={`save-content-${habit.key || habit.id}`}>
                <Save size={12} /> Save
              </button>
              <button className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1"
                      onClick={() => { setDesc(habit.description || ""); setNotes(habit.notes || ""); setEditContent(false); }}>
                <X size={12} /> Cancel
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [habits, setHabits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [lb, setLb] = useState(null);
  const [loading, setLoading] = useState(true);

  const [viewUser, setViewUser] = useState(user?.role === "admin" ? "adi" : user?.username);
  const viewingSelf = viewUser === user?.username;
  const today = todayISO();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, s, l] = await Promise.all([
        http.get(`/habits`, { params: { date: today, owner: viewUser } }),
        http.get(`/dashboard/${viewUser}`),
        http.get(`/leaderboard`),
      ]);
      setHabits(h.data.habits);
      setSummary(s.data);
      setLb(l.data);
    } catch (e) { toast.error(fmtErr(e)); }
    setLoading(false);
  }, [viewUser, today]);

  useEffect(() => { load(); }, [load]);

  async function mark(habit, status) {
    const map = { full: habit.full, partial: habit.partial, partial_low: habit.partial_low || 0, zero: 0 };
    const pts = habit.shadow ? 0 : (map[status] ?? 0);
    const prevPts = habit.log ? habit.log.points : 0;
    const diff = pts - prevPts;

    // Optimistic UI: update habit list only. Season score requires backend recompute
    // so we don't touch the score card locally (reload right after).
    setHabits((hs) => hs.map((h) => h.id === habit.id ? { ...h, log: { status, points: pts, marked_at: new Date().toISOString() } } : h));

    try {
      await http.post(`/habits/${habit.id}/log`, { date: today, status });
      const nice = { full: "FULL", partial: "PARTIAL", partial_low: "PARTIAL+", zero: "ZERO" }[status] || status;
      toast.success(`${habit.name}: ${nice}`);
      if (status === "full" && !habit.shadow) {
        confetti({ particleCount: 40, spread: 55, origin: { y: 0.7 }, colors: ["#16A34A", "#F59E0B", "#FBBF24"] });
        const remaining = habits.filter((x) => x.id !== habit.id && !x.shadow && x.log?.status !== "full");
        if (remaining.length === 0) {
          confetti({ particleCount: 180, spread: 100, origin: { y: 0.6 } });
          setTimeout(() => confetti({ particleCount: 120, spread: 120, origin: { x: 0.2, y: 0.7 } }), 200);
          setTimeout(() => confetti({ particleCount: 120, spread: 120, origin: { x: 0.8, y: 0.7 } }), 400);
          toast("PERFECT DAY!", { description: "+8 bonus locked in (up to weekly cap)" });
        }
      }
      // Reconcile with server (bonuses + week/season recomputed)
      load();
    } catch (e) {
      toast.error(fmtErr(e));
      load();
    }
  }

  async function updateWindow(habit, patch) {
    try {
      await http.patch(`/habits/${habit.id}/window`, patch);
      toast.success("Time updated");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  }

  async function updateContent(habit, patch) {
    try {
      await http.patch(`/habits/${habit.id}/content`, patch);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  }

  if (!user) return null;

  const completed = habits.filter((h) => h.log?.status === "full").length;

  // Countdown to challenge start (if in the future)
  const startISO = lb?.challenge?.start;
  const daysUntilStart = startISO ? Math.max(0, Math.ceil((new Date(startISO + "T00:00:00").getTime() - new Date(today + "T00:00:00").getTime()) / 86400000)) : 0;
  const preChallenge = daysUntilStart > 0;

  return (
    <div>
      {preChallenge && startISO && (
        <div className="nb-card p-5 sm:p-6 mb-6" data-testid="countdown-banner"
             style={{ background: "linear-gradient(135deg,#FEF3C7 0%,#FDE68A 100%)" }}>
          <div className="flex items-start sm:items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] sm:text-xs font-mono uppercase tracking-widest text-neutral-600">The countdown</div>
              <div className="font-display font-black text-2xl sm:text-4xl leading-tight">
                Challenge starts in <span className="text-adi">{daysUntilStart}</span> {daysUntilStart === 1 ? "day" : "days"}
              </div>
              <div className="text-sm text-neutral-700 mt-1">
                Day 1 · {new Date(startISO + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </div>
            </div>
            <div className="chip bg-white text-xs sm:text-sm">You can practice today. Streaks lock in from Day 1.</div>
          </div>
        </div>
      )}

      <HeadToHead lb={lb} />

      {user.role === "admin" && (
        <div className="flex gap-2 mb-6">
          <button onClick={() => setViewUser("adi")} className={`nb-btn px-3 py-2 flex items-center gap-2 ${viewUser === "adi" ? "bg-adi text-white" : "bg-white"}`} data-testid="view-adi">
            {lb?.adi?.avatar_url && <img src={lb.adi.avatar_url} alt="Adi" className="w-6 h-6 rounded-full border-2 border-ink object-cover" />}
            View Adi
          </button>
          <button onClick={() => setViewUser("kudrat")} className={`nb-btn px-3 py-2 flex items-center gap-2 ${viewUser === "kudrat" ? "bg-kudrat text-white" : "bg-white"}`} data-testid="view-kudrat">
            {lb?.kudrat?.avatar_url && <img src={lb.kudrat.avatar_url} alt="Kudrat" className="w-6 h-6 rounded-full border-2 border-ink object-cover" />}
            View Kudrat
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-8">
        <div className="lg:col-span-8 order-2 lg:order-1">
          <div className="nb-card p-4 sm:p-5 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="today-header">
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Today</div>
              <div className="font-display font-black text-lg sm:text-2xl md:text-3xl leading-tight">{fmtDateFull(today)}</div>
              <div className="text-[10px] text-neutral-500 font-mono mt-1">{today} · {nowHHMM()}</div>
            </div>
            <div className="chip bg-gold shrink-0">
              {completed}/{habits.length} done
            </div>
          </div>

          {loading ? <div className="nb-card p-6">Loading…</div> : (
            habits.length === 0 ? <div className="nb-card p-6">No habits active today.</div> :
            <div className="grid gap-3 sm:gap-4">
              {habits.map((h) => (
                <HabitCard key={h.id} habit={h} streak={summary?.streaks?.[h.id] || 0}
                  onMark={mark} onUpdateWindow={updateWindow} onUpdateContent={updateContent}
                  canMark={viewingSelf || user.role === "admin"}
                  canEditContent={viewingSelf && user.role !== "admin"} />
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-4 order-1 lg:order-2 flex flex-col gap-3 sm:gap-4">
          <SeasonScoreCard summary={summary} user={user} />
          <WeekScoreCard summary={summary} />
          <WeeksStrip summary={summary} />
          <GroupStreaksCard summary={summary} />
          <div className="nb-card p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-3"><Target size={18} /><h3 className="font-display font-bold text-base sm:text-lg">Recent bonuses</h3></div>
            {summary?.bonuses?.length ? (
              <ul className="space-y-2">
                {summary.bonuses.slice(0, 8).map((b, i) => (
                  <li key={`${b.date}-${b.reason}-${i}`} className="flex items-center justify-between text-sm gap-2">
                    <span className="truncate">
                      <span className="text-neutral-400 font-mono text-[10px] mr-1">{b.date.slice(5)}</span>
                      {b.reason}
                    </span>
                    <span className={`chip shrink-0 ${b.points >= 0 ? "bg-emerald-100" : "bg-rose-100"}`}>+{Number(b.points).toFixed(0)}</span>
                  </li>
                ))}
              </ul>
            ) : <div className="text-neutral-500 text-sm">Complete habits to earn bonuses.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
